import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/data-table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Webhook,
  Plus,
  Copy,
  Trash2,
  Eye,
  EyeOff,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Loader2,
  Bell,
  Activity,
} from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export default function WebhooksPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('endpoints');
  const [showEndpointDialog, setShowEndpointDialog] = useState(false);
  const [showSecretDialog, setShowSecretDialog] = useState(false);
  const [newSecret, setNewSecret] = useState('');
  const [selectedEndpoint, setSelectedEndpoint] = useState<any>(null);

  // Fetch endpoints
  const { data: endpoints = [], isLoading: endpointsLoading } = useQuery({
    queryKey: ['webhook-endpoints'],
    queryFn: () => api.getWebhookEndpoints(),
  });

  // Fetch event types
  const { data: eventTypesData } = useQuery({
    queryKey: ['webhook-event-types'],
    queryFn: () => api.getWebhookEventTypes(),
  });

  // Fetch deliveries
  const { data: deliveries = [] } = useQuery({
    queryKey: ['webhook-deliveries'],
    queryFn: () => api.getWebhookDeliveries(),
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['webhook-stats'],
    queryFn: () => api.getWebhookStats(),
  });

  // Create endpoint mutation
  const createEndpointMutation = useMutation({
    mutationFn: (data: any) => api.createWebhookEndpoint(data),
    onSuccess: (data) => {
      toast({ title: 'Webhook endpoint created' });
      setShowSecretDialog(true);
      setNewSecret(data.secret_key);
      queryClient.invalidateQueries({ queryKey: ['webhook-endpoints'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Delete endpoint mutation
  const deleteEndpointMutation = useMutation({
    mutationFn: (id: string) => api.deleteWebhookEndpoint(id),
    onSuccess: () => {
      toast({ title: 'Endpoint deleted' });
      queryClient.invalidateQueries({ queryKey: ['webhook-endpoints'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Regenerate secret mutation
  const regenerateSecretMutation = useMutation({
    mutationFn: (id: string) => api.regenerateWebhookSecret(id),
    onSuccess: (data) => {
      setShowSecretDialog(true);
      setNewSecret(data.secret_key);
      toast({ title: 'Secret regenerated' });
      queryClient.invalidateQueries({ queryKey: ['webhook-endpoints'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Retry delivery mutation
  const retryDeliveryMutation = useMutation({
    mutationFn: (id: string) => api.retryWebhookDelivery(id),
    onSuccess: () => {
      toast({ title: 'Retry initiated' });
      queryClient.invalidateQueries({ queryKey: ['webhook-deliveries'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copied to clipboard' });
  };

  const endpointColumns = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }: any) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{row.name}</span>
          {!row.is_verified && (
            <Badge variant="outline" className="text-yellow-600">Unverified</Badge>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'url',
      header: 'URL',
      cell: ({ row }: any) => (
        <span className="text-sm text-muted-foreground truncate max-w-xs block">
          {row.url}
        </span>
      ),
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      cell: ({ row }: any) => (
        <Badge variant={row.is_active ? 'default' : 'secondary'}>
          {row.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      accessorKey: 'subscriptions',
      header: 'Events',
      cell: ({ row }: any) => (
        <Badge variant="outline">{row.subscriptions?.length || 0} events</Badge>
      ),
    },
    {
      id: 'actions',
      cell: ({ row }: any) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleCopy(row.secret_key)}
            title="Copy Secret"
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => regenerateSecretMutation.mutate(row.endpoint_id)}
            title="Regenerate Secret"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              if (confirm('Delete this endpoint?')) {
                deleteEndpointMutation.mutate(row.endpoint_id);
              }
            }}
            title="Delete"
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      ),
    },
  ];

  const deliveryColumns = [
    {
      accessorKey: 'created_at',
      header: 'Time',
      cell: ({ row }: any) => new Date(row.created_at).toLocaleString(),
    },
    {
      accessorKey: 'event_type',
      header: 'Event',
      cell: ({ row }: any) => (
        <Badge variant="outline">{row.event_type}</Badge>
      ),
    },
    {
      accessorKey: 'delivery_status',
      header: 'Status',
      cell: ({ row }: any) => {
        const statusConfig: Record<string, { icon: any; color: string }> = {
          success: { icon: CheckCircle2, color: 'text-green-600' },
          failed: { icon: XCircle, color: 'text-red-600' },
          pending: { icon: Loader2, color: 'text-yellow-600' },
          retrying: { icon: RefreshCw, color: 'text-blue-600' },
        };
        const config = statusConfig[row.delivery_status] || statusConfig.pending;
        const Icon = config.icon;
        return (
          <div className={`flex items-center gap-1 ${config.color}`}>
            <Icon className="h-4 w-4" />
            <span>{row.delivery_status}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'response_status',
      header: 'Response',
      cell: ({ row }: any) => row.response_status || '-',
    },
    {
      accessorKey: 'attempt_number',
      header: 'Attempts',
      cell: ({ row }: any) => `${row.attempt_number}/${row.max_attempts}`,
    },
    {
      id: 'actions',
      cell: ({ row }: any) => (
        row.delivery_status === 'failed' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => retryDeliveryMutation.mutate(row.delivery_id)}
          >
            <RefreshCw className="mr-1 h-4 w-4" />
            Retry
          </Button>
        )
      ),
    },
  ];

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Webhook className="h-8 w-8" />
            Webhooks
          </h1>
          <p className="text-muted-foreground">
            Configure webhook endpoints for event notifications
          </p>
        </div>
        <Button onClick={() => setShowEndpointDialog(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Endpoint
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Endpoints</p>
                <p className="text-2xl font-bold">{stats?.active_endpoints || 0}</p>
              </div>
              <Webhook className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Deliveries</p>
                <p className="text-2xl font-bold">{stats?.total_deliveries || 0}</p>
              </div>
              <Activity className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Success Rate</p>
                <p className="text-2xl font-bold">{stats?.success_rate || 0}%</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Failed</p>
                <p className="text-2xl font-bold">{stats?.failed_deliveries || 0}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="endpoints">Endpoints</TabsTrigger>
          <TabsTrigger value="event-types">Event Types</TabsTrigger>
          <TabsTrigger value="deliveries">Delivery Log</TabsTrigger>
        </TabsList>

        <TabsContent value="endpoints">
          <Card>
            <CardHeader>
              <CardTitle>Webhook Endpoints</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={endpointColumns}
                data={endpoints || []}
                isLoading={endpointsLoading}
                searchKey="name"
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="event-types">
          <Card>
            <CardHeader>
              <CardTitle>Available Event Types</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {eventTypesData?.event_types?.map((event: string) => (
                  <div key={event} className="flex items-center gap-2 p-3 border rounded-lg">
                    <Bell className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-mono">{event}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="deliveries">
          <Card>
            <CardHeader>
              <CardTitle>Delivery Log</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={deliveryColumns}
                data={deliveries || []}
                isLoading={endpointsLoading}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Endpoint Dialog */}
      <Dialog open={showEndpointDialog} onOpenChange={setShowEndpointDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Webhook Endpoint</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.target as HTMLFormElement;
              const data = {
                name: (form.elements.namedItem('name') as HTMLInputElement).value,
                url: (form.elements.namedItem('url') as HTMLInputElement).value,
                description: (form.elements.namedItem('description') as HTMLInputElement).value,
                event_types: [],
              };
              createEndpointMutation.mutate(data);
              setShowEndpointDialog(false);
            }}
          >
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium">Name</label>
                <Input name="name" placeholder="My Webhook" required />
              </div>
              <div>
                <label className="text-sm font-medium">URL</label>
                <Input name="url" placeholder="https://example.com/webhook" type="url" required />
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <Input name="description" placeholder="Optional description" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setShowEndpointDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">Create Endpoint</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Secret Dialog */}
      <Dialog open={showSecretDialog} onOpenChange={setShowSecretDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Save Your Secret Key</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Important:</strong> Copy and save this secret key now. You won't be able to see it again.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Secret Key</label>
              <div className="flex items-center gap-2 mt-1">
                <Input value={newSecret} readOnly className="font-mono" />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleCopy(newSecret)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowSecretDialog(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
